package com.siriusxm.pia.views.favorites

import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import com.siriusxm.cmc.utils.ResizeParams
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.Application
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.ImageRenderingHelper
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.dom.*

/**
 * Displays the user's favorited entities on the home page.
 *
 * Uses cached entity data from FavoritesState for immediate display without loading.
 * Entity data is cached in localStorage and refreshed from server in background.
 *
 * If there are no favorites, nothing is rendered (the section is hidden entirely).
 */
@Composable
fun favoritesSection() {
    val favorites = rememberFavorites()
    val scope = rememberCoroutineScope()

    // Don't render anything if there are no favorites
    if (favorites.isInitialized.value && favorites.favoritedIds.value.isEmpty()) {
        return
    }

    // Don't show while waiting for initial load if we have no cached favorites
    if (!favorites.isInitialized.value) {
        return
    }

    // Don't show if we have no cached entity data yet
    val entities = favorites.cachedEntities.value
    if (entities.isEmpty() && favorites.favoritedIds.value.isNotEmpty()) {
        // Still loading entity data, but don't show spinner
        return
    }

    box({
        title = "Favorites"
        paddedContent = false
    }) {
        Div({
            style {
                backgroundColor(SXMUI.containerContentBackgroundColor.value())
            }
        }) {
            entities.forEach { entity ->
                favoriteEntityItem(entity) {
                    scope.launch {
                        val entityId = createEntityId(entity.type, entity.id)
                        favorites.removeFavorite(entityId)
                    }
                }
            }
        }
    }
}

/**
 * Displays a single favorited entity with its image, name, type, and a remove button (top right).
 *
 * @param entity The cached entity data to display
 * @param onRemove Callback when the remove button is clicked
 */
@Composable
private fun favoriteEntityItem(entity: CachedEntity, onRemove: () -> Unit) {
    A(href = "#aggregator/entity/${encodeURIComponent(entity.id)}", {
        style {
            position(Position.Relative)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
            padding(12.px, 16.px)
            textDecoration("none")
            color(SXMUI.textColor.value())
            property("border-bottom", "1px solid ${SXMUI.borderDividerDefault.value()}")
            cursor("pointer")
        }
        attr("class", "favorite-item")
    }) {
        // Star button - positioned top right, visible on hover (always starred since it's in favorites)
        Div({
            style {
                position(Position.Absolute)
                top(8.px)
                right(8.px)
                padding(4.px)
                cursor("pointer")
                borderRadius(4.px)
                opacity(1)
                property("transition", "opacity 0.15s ease")
            }
            attr("class", "favorite-star")
            title("Remove from favorites")
            onClick { event ->
                event.preventDefault()
                event.stopPropagation()
                onRemove()
            }
        }) {
            svgIcon(SVGIcons.starFilled) {
                size = 20.px
                style {
                    color(Color("#FFD700"))
                }
            }
        }

        // Entity image
        Div({
            style {
                width(48.px)
                height(48.px)
                flexShrink(0)
                borderRadius(4.px)
                overflow("hidden")
                backgroundColor(SXMUI.borderDividerDefault.value())
            }
        }) {
            entity.thumbnailUrl?.let { url ->
                val imageUrl = ImageRenderingHelper.getImageUrl(
                    Application.configuration.atlasImageServerBaseUrl,
                    url,
                    ResizeParams(48, 48)
                )
                Img(src = imageUrl) {
                    style {
                        width(100.percent)
                        height(100.percent)
                        property("object-fit", "cover")
                    }
                }
            }
        }

        // Entity info
        Div({
            style {
                flex(1)
                overflow("hidden")
            }
        }) {
            // Type label
            Div({
                style {
                    fontSize(11.px)
                    color(SXMUI.subtleTextColor.value())
                    property("text-transform", "uppercase")
                    marginBottom(2.px)
                }
            }) {
                Text(entity.type.replace("-", " "))
            }

            // Entity name
            Div({
                style {
                    fontWeight(500)
                    overflow("hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                }
            }) {
                Text(entity.name)
            }
        }
    }
}

/**
 * Styles for favorite items.
 */
object FavoritesStyles : StyleSheet() {
    val starButton by style {
        position(Position.Absolute)
        top(8.px)
        right(8.px)
        padding(4.px)
        cursor("pointer")
        borderRadius(4.px)
        opacity(0)
        property("transition", "opacity 0.15s ease")

        self + hover style {
            backgroundColor(SXMUI.borderDividerDefault.value())
        }
    }

    val starButtonVisible by style {
        opacity(1)
    }

    init {
        "a.favorite-item:hover" style {
            backgroundColor(SXMUI.borderDividerDefault.value())
        }

        // Show star on hover
        "a.favorite-item:hover .favorite-star" style {
            opacity(1)
        }
    }
}

/**
 * Standalone favorites page accessible at #favorites.
 * This page is not linked from the navigation but can be accessed directly for testing.
 */
@Composable
fun favoritesPage() {
    Style(FavoritesStyles)

    serviceView {
        serviceContentView {
            Div({
                style {
                    maxWidth(600.px)
                }
            }) {
                favoritesSection()
            }
        }
    }
}
